import { HttpClient, HttpHeaders, HttpResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { OfferTypesConstants } from "@app/csv/constants/offer-types.constants";
import { IdentificationTypeConstants } from "@shared/constants/identificationType.constants";
import { CreatedOrdersResponse } from "@shared/DTO/createdOrdersResponse";
import { Management } from "@shared/DTO/management";
import { Order } from "@shared/DTO/order";
import { Subject } from "@shared/DTO/subject";
import { SearchResultModel } from "@shared/models/search-result.model";
import { ConfigurationService } from "@shared/services/configuration.service";
import { head } from "lodash";
import * as moment from "moment";
import { Observable, of } from "rxjs";
import { map, switchMap } from "rxjs/operators";
import { CreatedOrdersResponseConverter } from "../converters/created-orders-response.converter";
import { OrderConverter } from "../converters/order.converter";
import { OrdersResearchConverter } from "../converters/orders-research.converter";
import { SubjectConverter } from "../converters/subject.converter";
import { CreatedOrdersResponseModel } from "../models/created-orders-response.model";
import { OrderModel } from "../models/order.model";
import { OrdersResearchModel } from "../models/orders-research.model";
import { SubjectModel } from "../models/subject.model";

@Injectable()
export class OrdersService {
  constructor(
    private httpClient: HttpClient,
    private orderConverter: OrderConverter,
    private ordersResearchConverter: OrdersResearchConverter,
    private createdOrdersResponseConverter: CreatedOrdersResponseConverter,
    private configurationService: ConfigurationService,
    private subjectConverter: SubjectConverter
  ) {}

  // ricerca ordini(prodotti)
  find(
    ordersResearch: OrdersResearchModel
  ): Observable<SearchResultModel<OrderModel, OrdersResearchModel>> {
    const url = this.getBaseResourceEndpoint() + `/search`;
    let params = this.ordersResearchConverter.convertToDto(ordersResearch);
    return this.httpClient.post(url, params).pipe(
      map((dtos: any) => {
        return this.orderConverter.convertToSearchModel(dtos);
      })
    );
  }

  // download lista ordini 
  download(
    ordersResearch: OrdersResearchModel
  ): Observable<ArrayBuffer> {
    const url = this.getBaseResourceEndpoint() + `/search/export`;
    let body = this.ordersResearchConverter.convertToDto(ordersResearch);

    return this.httpClient.post(url, body, {
      responseType: 'arraybuffer'
    })
  }

  // get ordine by id
  getById(idOrder: string | number): Observable<OrderModel> {
    const url = this.getBaseResourceEndpoint() + `/${idOrder}`;
    return this.httpClient.get(url).pipe(
      map((dtos: Order) => {
        return this.orderConverter.convertToModel(dtos);
      })
    );
  }

  createOrder(order: OrderModel): Observable<OrderModel> {
    let params = this.orderConverter.convertToDto(order);

    const url = this.getBaseResourceEndpoint();
    return this.httpClient.post(url, params).pipe(
      map((dtos: Order) => {
        return this.orderConverter.convertToModel(dtos);
      })
    );
  }

  createOrderFromCSV(
    orderData: Map<any, any>,
    file
  ): Observable<CreatedOrdersResponseModel> {
    let headers: HttpHeaders = new HttpHeaders()
      .set("verify", orderData.get("verify"))
      .set("orderType", orderData.get("orderType"))
      .set("opCustomerCode", orderData.get("opCustomerCode"))
      .set("opAccountCode", orderData.get("opAccountCode"))
      .set("opManagement", orderData.get("opManagement"))
      .set("opArticle", orderData.get("opArticle"))
      .set("opLanguage", orderData.get("opLanguage"))
      .set("Content-Type", "text/csv");

    if (orderData.get("opUnit")) {
      headers.append("opUnit", orderData.get("opUnit"));
    }

    const url = this.getBaseResourceEndpoint() + `/import/csv`;

    return this.httpClient.post(url, file, { headers: headers }).pipe(
      map((dtos: CreatedOrdersResponse) => {
        console.log(dtos);
        return this.createdOrdersResponseConverter.convertToModel(dtos);
      })
    );
  }

  updateSubjectData(
    orderId: string | number,
    subject: SubjectModel
  ): Observable<OrderModel> {
    return this.httpClient
      .put<Subject>(
        `${this.getBaseResourceEndpoint()}/${orderId}/subject`,
        this.subjectConverter.convertToDto(subject)
      )
      .pipe(switchMap(() => this.getById(orderId)));
  }

  downloadMassiveCsvTemplate({
    opServices,
    opArticle,
    opAccountCode,
    opCustomerCode,
    opCertDescriptionType,
  }: {
    opServices?: string;
    opArticle: string;
    opAccountCode: string;
    opCustomerCode: string;
    opCertDescriptionType: string;
  }): Observable<undefined | HttpResponse<Blob>> {
    let obs: Observable<undefined | HttpResponse<Blob>> = of();
    if (opArticle && opAccountCode && opCustomerCode) {
      const url = `${this.getBaseResourceEndpoint()}/import/csv/template`;
      let headers: HttpHeaders;
      if (opServices.includes(OfferTypesConstants.VIRTUAL)) {
        headers = new HttpHeaders()
          .set("orderType", "DIGITAL_SIGN_CFA")
          .set("opCustomerCode", opCustomerCode)
          .set("opAccountCode", opAccountCode)
          .set("opManagement", Management.RESELLER)
          .set("opArticle", opArticle)
          .set("opCertDescriptionType", opCertDescriptionType)
          .set("Content-Type", "text/csv");
      } else {
        headers = new HttpHeaders()
          .set("orderType", "DIGITAL_SIGN_CFA")
          .set("opCustomerCode", opCustomerCode)
          .set("opAccountCode", opAccountCode)
          .set("opManagement", Management.RESELLER)
          .set("opArticle", opArticle)
          .set("opCertDescriptionType", opCertDescriptionType)
          .set("Content-Type", "text/csv")
          .set("opServices", opServices);
      }

      obs = this.httpClient.get(url, {
        headers: headers,
        responseType: "blob",
        observe: "response",
      });
    }
    return obs;
  }

  verifyUploadMassiveCsv({
    opServices,
    opArticle,
    opAccountCode,
    opCustomerCode,
    opCertDescriptionType,
    csv,
    verify = false,
    contract,
    formData,
  }: {
    opServices: string;
    opArticle: string;
    opAccountCode: string;
    opCustomerCode: string;
    opCertDescriptionType: string;
    csv: string;
    verify: boolean;
    contract?: {
      base64: string;
      filename: string;
    };
    formData?: {
      name: string;
      surname: string;
      fiscalCode: string;
      externalCommonOrdersId: string;
    };
  }) {
    let obs: Observable<any> = of();
    if (opServices && opArticle && opAccountCode && opCustomerCode && csv) {
      const url = `${this.getBaseResourceEndpoint()}/import/csv?verify=${verify}`;
      let headers: HttpHeaders;
      if (opServices.includes(OfferTypesConstants.VIRTUAL)) {
        if (opCertDescriptionType !== "") {
          headers = new HttpHeaders()
            .set("orderType", "DIGITAL_SIGN_CFA")
            .set("opCustomerCode", opCustomerCode)
            .set("opAccountCode", opAccountCode)
            .set("opManagement", Management.RESELLER)
            .set("opArticle", opArticle)
            .set("opBooking", "true")
            .set("opLanguage", "ES")
            .set("opIdentificationType", IdentificationTypeConstants.OFFLINE)
            .set("opCertDescriptionType", opCertDescriptionType);
        } else {
          headers = new HttpHeaders()
            .set("orderType", "DIGITAL_SIGN_CFA")
            .set("opCustomerCode", opCustomerCode)
            .set("opAccountCode", opAccountCode)
            .set("opManagement", Management.RESELLER)
            .set("opArticle", opArticle)
            .set("opBooking", "true")
            .set("opLanguage", "ES")
            .set("opIdentificationType", IdentificationTypeConstants.OFFLINE);
        }
      } else {
        if (opCertDescriptionType !== "") {
          headers = new HttpHeaders()
            .set("orderType", "DIGITAL_SIGN_CFA")
            .set("opCustomerCode", opCustomerCode)
            .set("opAccountCode", opAccountCode)
            .set("opServices", opServices)
            .set("opManagement", Management.RESELLER)
            .set("opArticle", opArticle)
            .set("opBooking", "true")
            .set("opLanguage", "ES")
            .set("opIdentificationType", IdentificationTypeConstants.OFFLINE)
            .set("opCertDescriptionType", opCertDescriptionType);
        } else {
          headers = new HttpHeaders()
            .set("orderType", "DIGITAL_SIGN_CFA")
            .set("opCustomerCode", opCustomerCode)
            .set("opAccountCode", opAccountCode)
            .set("opServices", opServices)
            .set("opManagement", Management.RESELLER)
            .set("opArticle", opArticle)
            .set("opBooking", "true")
            .set("opLanguage", "ES")
            .set("opIdentificationType", IdentificationTypeConstants.OFFLINE);
        }
      }
      //.set("Content-Type", "multipart/form-data"); // must not be set for multipart or won't work with this version of angular
      const fd = new FormData();
      fd.append("csv", csv);
      if (!verify) {
        const { base64 = "", filename = "" } = contract || {};
        const {
          name = "",
          surname = "",
          fiscalCode = "",
          externalCommonOrdersId = "",
        } = formData || {};
        headers = headers
          .set("nameIR", name)
          .set("surnameIR", surname)
          .set("taxCodeIR", fiscalCode)
          .set("externalCommonOrdersId", externalCommonOrdersId);
        fd.append(
          "contractBase64",
          base64.replace("data:application/pdf;base64,", "")
        );
        fd.append("contractFilename", filename);
      }
      obs = this.httpClient.post(url, fd, {
        headers: headers,
      });
    }
    return obs;
  }

  findMassiveOrders(
    ordersMassiveResearch: OrdersResearchModel
  ): Observable<SearchResultModel<any, OrdersResearchModel>> {
    const url = this.getBaseResourceEndpoint() + `/search`;
    let params = this.ordersResearchConverter.convertToDto(
      ordersMassiveResearch
    );
    return this.httpClient.post(url, params).pipe(
      map((dtos: any) => {
        return this.orderConverter.convertToSearchModel(dtos);
      })
    );
  }

  downloadOrdersCSV(file: ArrayBuffer) {
    let blob = new Blob([file],{type:'text/csv'});
    let today = moment().format('YYYY-MM-DD')   
    const filename = `export-orders-${today}.csv`;
    let url = window.URL.createObjectURL(blob);
    let anchor = document.createElement("a");
    anchor.download = filename;
    anchor.href = url;
    anchor.click();
    window.URL.revokeObjectURL(anchor.href);
  }


  private getBaseResourceEndpoint(): string {
    return this.configurationService.configuration.api.orders;
  }
}
